import React, { createContext, useContext, useState } from "react";
import { useReportHistoryByUID } from "../hooks/useReportHistory";

const ReportComparisonContext = createContext();

export const useReportComparison = () => {
  const context = useContext(ReportComparisonContext);
  if (!context) {
    throw new Error(
      "useReportComparison must be used within a ReportComparisonProvider",
    );
  }
  return context;
};

export const ReportComparisonProvider = ({
  children,
  currentReport,
  organizationId,
}) => {
  const [isComparisonEnabled, setIsComparisonEnabled] = useState(false);
  const [selectedPreviousReportId, setSelectedPreviousReportId] =
    useState(null);

  // Debug current report data


  // Fetch previous reports for this client
  const { reports: previousReports, loading: loadingReports } =
    useReportHistoryByUID(
      currentReport?.clientData?.uid,
      organizationId,
      10, // Limit to last 10 reports
    );

  // Filter out the current report from previous reports
  const availablePreviousReports = previousReports.filter(
    (report) => report.id !== currentReport?.id,
  );

  // Debug logging


  // Get the selected previous report
  const selectedPreviousReport = availablePreviousReports.find(
    (report) => report.id === selectedPreviousReportId,
  );

  // Auto-select the most recent previous report when comparison is first enabled
  const enableComparison = () => {
    setIsComparisonEnabled(true);
    if (!selectedPreviousReportId && availablePreviousReports.length > 0) {
      setSelectedPreviousReportId(availablePreviousReports[0].id);
    }
  };

  const disableComparison = () => {
    setIsComparisonEnabled(false);
    setSelectedPreviousReportId(null);
  };

  const selectPreviousReport = (reportId) => {
    setSelectedPreviousReportId(reportId);
  };

  const value = {
    // State
    isComparisonEnabled,
    selectedPreviousReportId,
    selectedPreviousReport,
    availablePreviousReports,
    loadingReports,
    currentReport,

    // Actions
    enableComparison,
    disableComparison,
    selectPreviousReport,
    setIsComparisonEnabled: (enabled) => {
      if (enabled) {
        enableComparison();
      } else {
        disableComparison();
      }
    },

    // Computed
    hasAvailableReports: availablePreviousReports.length > 0,
    canCompare: isComparisonEnabled && selectedPreviousReport,
  };

  return (
    <ReportComparisonContext.Provider value={value}>
      {children}
    </ReportComparisonContext.Provider>
  );
};
